import { useState, useCallback } from 'react';

export function useGeocode() {
  const [geocodeCache, setGeocodeCache] = useState({});
  const [loading, setLoading] = useState(false);

  /**
   * Converte lat/lng para endereço usando Google Maps Geocoding API
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {Promise<string>} - Endereço formatado
   */
  const getAddressFromCoords = useCallback(async (lat, lng) => {
    // Verifica cache primeiro
    const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
    if (geocodeCache[cacheKey]) {
      return geocodeCache[cacheKey];
    }

    try {
      setLoading(true);

      // Usa o objeto global window.google.maps se disponível
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();

        return new Promise((resolve, reject) => {
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results.length > 0) {
              // Pega o primeiro resultado (mais relevante)
              const address = results[0].formatted_address;

              // Salva no cache
              setGeocodeCache((prev) => ({
                ...prev,
                [cacheKey]: address,
              }));

              resolve(address);
            } else {
              // Fallback para coordenadas se não conseguir endereço
              const fallback = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
              setGeocodeCache((prev) => ({
                ...prev,
                [cacheKey]: fallback,
              }));
              resolve(fallback);
            }
          });
        });
      } else {
        // Se Google Maps não estiver disponível
        const fallback = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        setGeocodeCache((prev) => ({
          ...prev,
          [cacheKey]: fallback,
        }));
        return fallback;
      }
    } catch (err) {
      console.error('Erro ao geocodificar:', err);
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    } finally {
      setLoading(false);
    }
  }, [geocodeCache]);

  /**
   * Geocodifica múltiplos pontos de uma vez
   * @param {Array<{lat, lng}>} locations - Array de localizações
   * @returns {Promise<Array>} - Array com localizações e endereços
   */
  const geocodeMultipleLocations = useCallback(async (locations) => {
    try {
      const results = await Promise.all(
        locations.map(async (location) => {
          const address = await getAddressFromCoords(location.lat, location.lng);
          return {
            ...location,
            address,
          };
        })
      );
      return results;
    } catch (err) {
      console.error('Erro ao geocodificar múltiplas localizações:', err);
      return locations;
    }
  }, [getAddressFromCoords]);

  /**
   * Limpa o cache de geocodificação
   */
  const clearCache = useCallback(() => {
    setGeocodeCache({});
  }, []);

  return {
    getAddressFromCoords,
    geocodeMultipleLocations,
    clearCache,
    loading,
    cacheSize: Object.keys(geocodeCache).length,
  };
}
